// DST-correcte Europe/Amsterdam wall-clock ↔ UTC helpers.
//
// Alle functies zijn puur (geen I/O, geen side-effects) en gebruiken uitsluitend
// `Intl.DateTimeFormat` + `Date` — conform CLAUDE.md "geen datum-library".
//
// Tijdzone-conventie (CLAUDE.md §Tijdzone, locale & geld):
//   • De database slaat UTC op (Prisma DateTime → PostgreSQL timestamptz).
//   • UI, cron en CAO-regels rekenen in Europe/Amsterdam.
//   • Deze helpers vormen de brug tussen beide werelden.

// ─── Intern: Amsterdam-offset ────────────────────────────────────────────────

/**
 * Offset van Europe/Amsterdam t.o.v. UTC op het UTC-instant `utc`, in minuten.
 *
 * Methode: formatteer `utc` in Europe/Amsterdam via Intl, bouw een "naïef UTC"
 * Date terug uit de gerenderde componenten, en bereken het verschil.
 *   offset = renderedAsIfUtc − actualUtc
 *
 * Zomertijd (CEST, UTC+2) → +120. Wintertijd (CET, UTC+1) → +60.
 */
function amsterdamOffsetMinutes(utc: Date): number {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const parts = fmt.formatToParts(utc)
  const get = (type: string): number => {
    const p = parts.find((x) => x.type === type)
    if (!p) throw new Error(`Intl formatToParts mist veld "${type}"`)
    return parseInt(p.value, 10)
  }

  let hour = get('hour')
  // Intl kan '24' teruggeven voor midnight in sommige omgevingen — normaliseer.
  if (hour === 24) hour = 0

  const renderedAsIfUtcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second'),
  )

  return (renderedAsIfUtcMs - utc.getTime()) / 60000
}

// ─── Validatie-helpers ────────────────────────────────────────────────────────

/** Gooit als `ymd` niet het formaat 'yyyy-mm-dd' heeft of de datum niet bestaat. */
function parseYmd(ymd: string): { y: number; m: number; d: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())
  if (!match) {
    throw new Error(`Ongeldige ymd: "${ymd}". Verwacht "yyyy-mm-dd".`)
  }
  const y = parseInt(match[1], 10)
  const m = parseInt(match[2], 10)
  const d = parseInt(match[3], 10)
  // Rollover-check: Date.UTC normaliseert stille overflows (bijv. 32 jan → 1 feb).
  const check = new Date(Date.UTC(y, m - 1, d))
  if (
    check.getUTCFullYear() !== y ||
    check.getUTCMonth() !== m - 1 ||
    check.getUTCDate() !== d
  ) {
    throw new Error(`Ongeldige ymd: "${ymd}" bestaat niet in de kalender.`)
  }
  return { y, m, d }
}

/** Gooit als `hm` niet het formaat 'HH:mm' heeft of een ongeldige klokstand is. */
function parseHm(hm: string): { h: number; min: number } {
  const match = /^(\d{2}):(\d{2})$/.exec(hm.trim())
  if (!match) {
    throw new Error(`Ongeldige hm: "${hm}". Verwacht "HH:mm" (24-uurs, nul-gevuld).`)
  }
  const h = parseInt(match[1], 10)
  const min = parseInt(match[2], 10)
  if (h > 23 || min > 59) {
    throw new Error(`Ongeldige klokstand: "${hm}". Uren 0–23, minuten 0–59.`)
  }
  return { h, min }
}

// ─── Publieke API ─────────────────────────────────────────────────────────────

/**
 * UTC-instant voor een NL wall-clock (Europe/Amsterdam).
 *
 *   08:00 NL zomertijd  → 06:00Z  (UTC+2)
 *   08:00 NL wintertijd → 07:00Z  (UTC+1)
 *
 * Algoritme: twee-pass offset-solve (standaard, DST-correct).
 *   1. Eerste schatting: behandel de wall-clock als naïef UTC.
 *   2. Pas de Amsterdam-offset toe op de schatting → betere schatting.
 *   3. Verfijn eenmaal: pas de offset op de betere schatting toe.
 *
 * DST-vouwconventie (2026-10-25 02:00–03:00 ambigue zone):
 *   De naïeve schatting (wall-clock als UTC) valt ná de terugzetting (klokken
 *   gaan terug om 01:00Z / 03:00 NL). Daar is de offset al CET (+60 min), dus
 *   beide passes geven dezelfde correctie → de twee-pass levert de CET-zijde
 *   op (het TWEEDE voorkomende UTC-instant, UTC+1).
 *   Voorbeeld: 02:30 NL → 01:30Z (CET-zijde). Deterministisch, consistent.
 *
 * @param ymd  Datum als 'yyyy-mm-dd'.
 * @param hm   Tijd als 'HH:mm' (24-uurs, nul-gevuld).
 * @returns    UTC-instant.
 * @throws     Bij ongeldig formaat of niet-bestaande kalenderdatum.
 */
export function amsterdamWallclockToUtc(ymd: string, hm: string): Date {
  const { y, m, d } = parseYmd(ymd)
  const { h, min } = parseHm(hm)

  // Stap 1: eerste schatting — wall-clock behandeld als naïef UTC.
  const guess = Date.UTC(y, m - 1, d, h, min, 0, 0)

  // Stap 2: eerste correctie.
  const t1 = guess - amsterdamOffsetMinutes(new Date(guess)) * 60000

  // Stap 3: verfijn eenmaal (vangt DST-grenzen correct op).
  const t2 = guess - amsterdamOffsetMinutes(new Date(t1)) * 60000

  return new Date(t2)
}

/**
 * UTC-instant voor het begin van een hele dag in Europe/Amsterdam.
 *
 * Dit is 00:00 Europe/Amsterdam op `ymd`, uitgedrukt als UTC.
 *
 * @param ymd  Datum als 'yyyy-mm-dd'.
 * @returns    UTC-instant van 00:00 NL van die dag.
 */
export function amsterdamAllDayStartToUtc(ymd: string): Date {
  return amsterdamWallclockToUtc(ymd, '00:00')
}

/**
 * UTC-instant voor het EINDE van een hele dag in Europe/Amsterdam: 23:59:59.999
 * NL-tijd op `ymd`.
 *
 * Voor een termijn "tot en met {datum}": de dag telt helemaal mee, tot de laatste
 * milliseconde. Een deadline op 23:59:00 weigerde de laatste minuut, en een
 * deadline op 00:00 UTC de hele dag.
 *
 * @param ymd  Datum als 'yyyy-mm-dd'.
 * @returns    UTC-instant van 23:59:59.999 NL van die dag.
 */
export function amsterdamAllDayEndToUtc(ymd: string): Date {
  return new Date(amsterdamWallclockToUtc(ymd, '23:59').getTime() + 59_999)
}

/**
 * Converteert een NL-wandklok (datum 'YYYY-MM-DD' + tijd 'HH:mm') naar een UTC
 * ISO-string, los van de host-timezone.
 *
 * Dunne wrapper rond `amsterdamWallclockToUtc` (de DST-correcte twee-pass
 * offset-solve) die direct een ISO-string teruggeeft — handig voor form-payloads
 * die een `z.string().datetime()` verwachten. Een lege/ontbrekende tijd valt
 * terug op '00:00'.
 *
 *   ('2026-01-15', '08:30') wintertijd → '2026-01-15T07:30:00.000Z'
 *   ('2026-07-15', '08:30') zomertijd  → '2026-07-15T06:30:00.000Z'
 *   ('2026-01-15', '00:00')            → '2026-01-14T23:00:00.000Z'
 *
 * @param datum     Datum als 'YYYY-MM-DD'.
 * @param tijd      Tijd als 'HH:mm' (24-uurs); leeg → '00:00'.
 * @param timeZone  Tijdzone (default 'Europe/Amsterdam').
 * @throws          Bij ongeldig datum-/tijdformaat (zie parseYmd/parseHm).
 */
export function wallClockToUtcIso(
  datum: string,
  tijd: string,
  timeZone = 'Europe/Amsterdam',
): string {
  // Voor STREEP is de tijdzone altijd Europe/Amsterdam; de parameter bestaat
  // voor signatuur-compat. Andere zones zijn (nog) niet ondersteund.
  if (timeZone !== 'Europe/Amsterdam') {
    throw new Error(`wallClockToUtcIso ondersteunt alleen 'Europe/Amsterdam', kreeg "${timeZone}".`)
  }
  return amsterdamWallclockToUtc(datum, tijd && tijd.length > 0 ? tijd : '00:00').toISOString()
}

/**
 * NL wall-clock (Europe/Amsterdam) van een UTC-instant.
 *
 * Gebruikt voor weergave van datum/tijdlabels én als inverse van
 * `amsterdamWallclockToUtc` bij backfills.
 *
 * @param d  UTC-instant.
 * @returns  `{ ymd: 'yyyy-mm-dd', hm: 'HH:mm' }` in Europe/Amsterdam.
 */
export function utcToAmsterdamWallclock(d: Date): { ymd: string; hm: string } {
  // 'en-CA' met year/month/day geeft 'yyyy-mm-dd' terug — hetzelfde patroon
  // als in dagtaken-datum.ts (vandaagAmsterdam).
  const dateFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const timeFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const ymd = dateFmt.format(d)

  // Intl time-formatting: 'en-CA' hour12:false geeft 'HH:mm' terug (zero-padded).
  // Sommige Intl-implementaties geven 'HH:mm:ss' of laten een komma weg — we
  // extracten alleen de eerste twee delen (uren:minuten).
  const rawTime = timeFmt.format(d)
  // Normaliseer '24:xx' naar '00:xx' (Intl-edge in sommige engines).
  const normalized = rawTime.replace(/^24:/, '00:')
  // Neem alleen 'HH:mm' (eerste 5 tekens) voor het geval de formatter meer toevoegt.
  const hm = normalized.slice(0, 5)

  return { ymd, hm }
}

/**
 * NL uur-fractie voor de verticale positionering in een week-view.
 *
 * Retourneert `nlHour + nlMinute / 60` voor het NL wall-clock van `d`.
 *
 *   06:30Z zomertijd = 08:30 NL → 8.5
 *   07:15Z wintertijd = 08:15 NL → 8.25
 *
 * @param d  UTC-instant.
 * @returns  Uur-fractie in [0, 24).
 */
export function amsterdamHourFraction(d: Date): number {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const parts = fmt.formatToParts(d)
  const get = (type: string): number => {
    const p = parts.find((x) => x.type === type)
    if (!p) throw new Error(`Intl formatToParts mist veld "${type}"`)
    return parseInt(p.value, 10)
  }

  let hour = get('hour')
  if (hour === 24) hour = 0
  const minute = get('minute')

  return hour + minute / 60
}
